// Idle auto-logout (#1). After IDLE_MINUTES of no activity a warning modal counts down
// WARN_SECONDS; if the user doesn't respond we sign them out locally. Any activity while active
// resets the clock. During the warning, passive activity is ignored on purpose — the user must
// click "Stay signed in", otherwise a stray mousemove would silently cancel the logout.
// The clock is SHARED by every aboo tab / window (localStorage): working in one window keeps them
// all signed in, and "Stay signed in" in one dismisses the warning in the others. A per-window clock
// signed everyone out whenever one background window sat idle (Sami, 2026-10-05).
import { signal } from './t4.js';

const IDLE_MINUTES = 15;                       // inactivity before the warning shows
const WARN_SECONDS = 60;                       // countdown shown in the warning modal
const IDLE_MS = IDLE_MINUTES * 60 * 1000;
const EXPIRE_MS = IDLE_MS + WARN_SECONDS * 1000;

export const idleWarning   = signal(false);    // is the warning modal showing?
export const idleCountdown = signal(WARN_SECONDS);

const ACTIVITY = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
let lastActivity = Date.now();
let tick = null;
let onExpire = null;
const K_SHARED = 'sleek_idle_last_activity';   // the newest activity across every window (ms)
let lastShared = 0;                            // throttle: write the shared clock at most every 5s

function readShared(){
  try { return Number(localStorage.getItem(K_SHARED)) || 0; } catch { return 0; }
}
function writeShared(at, force){
  if(!force && at - lastShared < 5000) return;
  lastShared = at;
  try { localStorage.setItem(K_SHARED, String(at)); } catch { /* storage blocked — this window only */ }
}
function onActivity(){
  if(idleWarning.value) return;                // warning up → require an explicit choice
  lastActivity = Date.now();
  writeShared(lastActivity);
}

function loop(){
  const shared = readShared();
  if(shared > lastActivity){                   // activity (or "Stay signed in") in another window
    lastActivity = shared;
    if(idleWarning.value){ idleWarning.value = false; idleCountdown.value = WARN_SECONDS; }
  }
  const idle = Date.now() - lastActivity;
  if(!idleWarning.value){
    if(idle >= IDLE_MS){ idleWarning.value = true; idleCountdown.value = WARN_SECONDS; }
    return;
  }
  const left = Math.ceil((EXPIRE_MS - idle) / 1000);
  idleCountdown.value = Math.max(0, left);
  if(left <= 0){ const cb = onExpire; stopIdleWatch(); if(cb) cb(); }
}

export function stayActive(){                  // "Stay signed in" — dismiss + reset the clock
  idleWarning.value = false;
  idleCountdown.value = WARN_SECONDS;
  lastActivity = Date.now();
  writeShared(lastActivity, true);             // …in every window
}

export function startIdleWatch(expireCb){
  onExpire = expireCb;
  lastActivity = Date.now();
  writeShared(lastActivity, true);
  idleWarning.value = false;
  if(tick) return;                             // idempotent — shell() may call on every render
  ACTIVITY.forEach(ev => window.addEventListener(ev, onActivity, { passive: true }));
  tick = setInterval(loop, 1000);
}

export function stopIdleWatch(){
  if(tick){ clearInterval(tick); tick = null; }
  ACTIVITY.forEach(ev => window.removeEventListener(ev, onActivity));
  idleWarning.value = false;
}
